import { useEffect, useRef, useState } from "react";
import {
  AddTask,
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  Build,
  Check,
  CheckCircle,
  ChevronLeft,
  Close,
  ContentCopy,
  ControlCamera,
  ControlPoint,
  CopyAll,
  Dataset,
  DeveloperMode,
  DevicesOther,
  Edit,
  Error,
  Expand,
  ExpandMore,
  Fullscreen,
  FullscreenExit,
  Headset,
  History,
  ImportExport,
  Info,
  InfoOutlined,
  MenuBook,
  More,
  MoreHoriz,
  MoreHorizOutlined,
  MoreHorizSharp,
  MoreVert,
  Pause,
  PlayArrow,
  ReceiptLong,
  Replay,
  Settings,
  Share,
  Timelapse,
  Upload,
  VolumeDown,
  VolumeMute,
  VolumeUp,
  Warning,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  CssBaseline,
  Dialog,
  Divider,
  Drawer,
  Fade,
  FormControlLabel,
  FormGroup,
  Grow,
  Icon,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  MenuPaper,
  Modal,
  Paper,
  Popover,
  Select,
  Slide,
  Slider,
  Snackbar,
  SnackbarCloseReason,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import {
  closeSnackbar,
  SnackbarKey,
  useSnackbar,
  VariantType,
} from "notistack";
import { SyntheticEvent } from "react";
import {
  ACTIONS,
  ConnectionState,
  GCState,
  INCOMING_MESSAGES,
  IS_DEV,
} from "./states";
import {
  IPlaylistCollectionPayload,
  Level,
  Playlist,
  PlaylistCollection,
} from "./Playlists";
import PlaylistWindow from "./PlaylistWindow";
import Heading from "./Heading";
import HeadsetModal from "./HeadsetModal";
import Device from "./Device";
import RCSettings, { RCSetting } from "./Settings";
import SettingsWindow from "./SettingsWindow";
import InfoModal from "./InfoModal";
import { title } from "process";
import VolumeSlider from "./VolumeSlider";
import { Colors, glassColor, whiteGlassColor } from "./Colors";
import LocalSettings from "./LocalSettings";
import { BrowserView, isMobile, MobileView } from "react-device-detect";
import Draggable from "react-draggable";
import { networkInterfaces } from "os";

const signalingUrl = "ws://54.242.205.63";

const drawerWidth = "10%";

const CalibrationMode = {
  BasicStanding: "BasicStanding",
  LowerLimitsStanding: "LowerLimitsStanding",
  LowerLimitsSeated: "LowerLimitsSeated",
  AllStanding: "AllStanding",
  AllSeated: "AllSeated",
};

class LogItem {
  date: Date;

  constructor(public type: string, public message: string) {
    this.type = type;
    this.message = message;
    this.date = new Date();
  }

  toString() {
    return `[${this.type}] ${this.message} (${this.date.toLocaleString()})`;
  }
}

export class WarningMessage {
  title: string;
  message: string;

  constructor(title: string, message: string) {
    this.title = title;
    this.message = message;
  }
}

class GCStateObj {
  currentLevelNameMenu = "no level loaded";
  currentSongDisplayName = "no song loaded";
  catchingMode = "Casual";
  headsetUIenabled = true;
  systemVolume = 1.0;
  currentScore = 0;
  headsetScoreVisible = true;
  calibrationMode = "StandingBasic";
  isCasting = true;
  currentLevelTimeSeconds = -2.18181825;
  currentLevelDurationSeconds = 0.0;
  headsetID = "836cbz";
}

export default function VideoPlayer({
  localConnectionId: desiredConnectionId,
  abortState,
  setMainAppConnection,
}: {
  localConnectionId?: string;
  abortState: (state: boolean) => void;
  setMainAppConnection: (state: boolean) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);

  const [showStateInfo, setShowStateInfo] = useState(true);
  const [showText, setShowText] = useState(true);

  const [connectionIdMismatch, setConnectionIdMismatch] = useState(false);

  const [
    playlistPickerAnchorRef,
    setPlaylistPickerAnchorRef,
  ] = useState<null | HTMLElement>(null);

  const [
    settingsAnchorRef,
    setSettingsAnchorRef,
  ] = useState<null | HTMLElement>(null);

  const [versionOpen, openVersion] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const [connectionState, setConnectionState] = useState(
    ConnectionState.Disconnected
  );

  const [shouldRefresh, setShouldRefresh] = useState(true);

  const versionClose = (
    event: SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    openVersion(false);
  };

  const [showSettings, setShowSettings] = useState(false);

  const [failReason, setFailReason] = useState("Unknown");

  const [log, setLog] = useState<LogItem[]>([]);
  const [logOpen, setLogOpen] = useState(false);

  const [deviceLog, setDeviceLog] = useState<string[]>([]);
  const [deviceLogOpen, setDeviceLogOpen] = useState(false);

  const [gcMode, setGCMode] = useState(GCState.EXPECTING_GC_STATE);

  const [gcState, setGCState] = useState(new GCStateObj());

  const [allPlaylists, setAllPlaylists] = useState<PlaylistCollection>();

  const [pickingLevel, setPickingLevel] = useState(false);

  const paused = gcMode === GCState.CATCHING_PAUSED;

  const [copied, setCopied] = useState(false);

  const [newHeadset, setNewHeadset] = useState(false);

  const [settings, setSettings] = useState<RCSettings>(new RCSettings());

  const [showToolbarMobile, setShowToolbarMobile] = useState(true);
  const [showDrawerContentMobile, setShowDrawerContentMobile] = useState(false);

  const [showFix, setShowFix] = useState(false);

  const [warning, setWarning] = useState<WarningMessage>(
    new WarningMessage("", "")
  );

  const topLevelElement = useRef<HTMLDivElement>(null);

  const [moreAncholEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);

  function getLatestLogFile() {}

  function exportLog() {
    const header = "Groove Catcher Log";
    const exportDate = "Exported " + new Date().toLocaleString();
    const logEntries = log.map((entry) => entry.toString()).join("\n");
    const blob = new Blob([`${header}\n${exportDate}\n\n${logEntries}`], {
      type: "text/plain",
    });
    // open file browser to save the file
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "groove_catcher_log (" + new Date().toLocaleString() + ").txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    sendToast("Log exported", "success");
  }

  function logItem(type: string, message: string) {
    console.log(`[${type}] ${message}`);
    setLog((prev) => {
      const newLog = [...prev, new LogItem(type, message)];
      if (newLog.length > 100) {
        newLog.shift(); // Remove the oldest log entry
      }
      return newLog;
    });
  }

  //checks if the incoming connection ID matches the desired connection ID
  //this is done so that we can ignore messages coming from other headsets that we dont care about >:(
  function validateConnectionId(incomingId: string) {
    var validated = incomingId === desiredConnectionId;
    return validated;
  }

  function copy() {
    if (!desiredConnectionId) {
      didFail("No connection ID available to copy");
      return;
    }
    navigator.clipboard.writeText(desiredConnectionId).then(() => {
      sendToast("Connection ID copied to clipboard", "success");
      logItem(
        "info",
        "Connection ID copied to clipboard: " + desiredConnectionId
      );
    });
    setCopied(true);

    //reset the copy icon back from a checkmark to a copy icon
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }

  const [currentDevice, setCurrentDevice] = useState<Device>();

  const timeOut = useRef<ReturnType<typeof setTimeout> | null>(null);

  const streamRef = useRef<MediaStream>(new MediaStream());

  const [isFullscreen, setIsFullscreen] = useState(false);

  const sendToast = (
    message: string,
    variant: VariantType = "default",
    debugInfo?: string
  ) => {
    const action = (snackbarId: SnackbarKey) => (
      <>
        <IconButton
          color="secondary"
          onClick={() => {
            closeSnackbar(snackbarId);
          }}
        >
          <Close />
        </IconButton>
      </>
    );

    enqueueSnackbar(message, {
      action,
      variant: variant,
      autoHideDuration: 2000,
    });

    logItem(variant, message);
    if (debugInfo) {
      logItem(variant, debugInfo);
    }
  };

  const pendingIceCandidates: RTCIceCandidate[] = [];

  function setNewConnectionState(state: ConnectionState) {
    setConnectionState(state);
    console.log("Connection state changed to:", state);
    var variant = "";
    switch (state) {
      case ConnectionState.Error:
        variant = "error";
        break;
      case ConnectionState.Disconnected:
        variant = "warning";
        break;
      case ConnectionState.Connected:
        variant = "success";
        break;
    }
    logItem(variant, "Connection state changed to: " + state);
  }

  function didFail(
    reason: string,
    debugInfo?: string,
    disableReconnect: boolean = false
  ) {
    if (disableReconnect) {
      setConnectionIdMismatch(true);
    }
    setNewConnectionState(ConnectionState.Error);
    setFailReason(reason);
    console.error("Failed to connect:", reason);
    if (debugInfo) {
      console.error("Debug info:", debugInfo);
    }
    sendToast("Failed to connect: " + reason, "error", debugInfo);
  }

  const connectionStateRef = useRef(connectionState);

  let cancelButton = () => {
    return (
      <Button
        variant="contained"
        color="error"
        fullWidth
        sx={{ marginTop: "0.5em" }}
        onClick={() => {
          sendGCAction(ACTIONS.QUIT_CANCEL);
          logItem("info", "Canceling action");
        }}
      >
        Cancel
      </Button>
    );
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (showStateInfo) {
      timer = setTimeout(() => {
        setShowText(true);
      }, 200); // delay in milliseconds
    } else {
      setShowText(false); // reset immediately if trigger is turned off
    }

    return () => clearTimeout(timer); // cleanup on unmount or trigger change
  }, [showStateInfo]);

  useEffect(() => {
    connectionStateRef.current = connectionState;
  }, [connectionState]);

  useEffect(() => {
    window.history.replaceState({}, window.location.pathname, "?aborted=false");
  }, []);

  useEffect(() => {
    if (connectionState === ConnectionState.Disconnected) {
      const timer = setTimeout(() => {
        // Check once more before firing timeout logic.
        if (connectionStateRef.current === ConnectionState.Disconnected) {
          didFail(
            "Timeout: No connection established after 10 seconds",
            "Connection Timed out, this usually means the rc app failed to find Groove Catcher"
          );
        }
      }, 10000);

      // Cleanup: clear the timeout if connectionState changes or component unmounts.
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionState]);

  function setUpChannel(channel: RTCDataChannel) {
    console.log("Data channel created by peer:", channel);
    dcRef.current = channel;
    dcRef.current.onopen = () => {
      console.log("Data channel open!");
      logItem("success", "Data channel opened");
      newMessageTimeout();
    };
    dcRef.current.onmessage = (e) => {
      handleChannelMessage(e);
    };
    dcRef.current.onclose = () => {
      console.log("Data channel closed!");
      logItem("warning", "Data channel closed");
    };
  }

  function handleChannelMessage(event: MessageEvent) {
    const data = JSON.parse(event.data);
    const message = data.message;
    switch (message) {
      case INCOMING_MESSAGES.FULL_STATE: {
        var stateObj = data.dataObj;
        setGCState(stateObj);
        setGCModeById(stateObj.GCMode);
        sendCommandJSON("FullStateAck", "", []);
        break;
      }
      case INCOMING_MESSAGES.ALL_PLAYLISTS: {
        handlePlaylists(data.dataObj);
        break;
      }
      case INCOMING_MESSAGES.ALL_SETTINGS: {
        handleSettings(data.dataObj.settings);
        break;
      }
      case INCOMING_MESSAGES.GC_LOG: {
        handleLog(data.dataObj);
        break;
      }
      case INCOMING_MESSAGES.ABORT: {
        didFail(
          "Groove Catcher disconnected unexpectedly",
          "Received abort message from Groove Catcher, please retry connection"
        );
        disconnectFromGC(
          true,
          "Groove Catcher disconnected unexpectedly, please retry connection"
        );
        break;
      }
      case INCOMING_MESSAGES.CLOSING_CONNECTION: {
        didFail(
          "Groove Catcher quit unexpectedly",
          "Received closing connection message from Groove Catcher, please retry connection"
        );
        disconnectFromGC(
          true,
          "Groove Catcher was closed or quit unexpectedly"
        );
        break;
      }
    }

    if (timeOut.current) {
      clearTimeout(timeOut.current);
    }

    newMessageTimeout();
  }

  function handleLog(log: any) {
    var arrayFromJson: string[] = JSON.parse(log);
    setDeviceLog(arrayFromJson);
  }

  function checkWarningState(gcMode: any) {
    if (
      gcMode === GCState.SYSTEM_PAUSED ||
      gcMode === GCState.SYSTEM_MENU_ACTIVE ||
      gcMode === GCState.SYSTEM_OUT_OF_GUARDIAN
    ) {
      if (connectionStateRef.current !== ConnectionState.Warning) {
        setNewConnectionState(ConnectionState.Warning);
        sendToast("Warning: " + gcMode.displayName, "warning");
        setWarningMessage(gcMode);
      }
    } else {
      if (connectionStateRef.current === ConnectionState.Warning) {
        sendToast("Warning fixed, connection restored", "success");
        setNewConnectionState(ConnectionState.Connected);
      }
    }
  }

  function handleSettings(data: any) {
    settings.settings = [];

    for (var setting of data) {
      var rcSetting: RCSetting = RCSettingFromDataObjSetting(setting);
      settings.settings.push(rcSetting);
    }
  }

  function RCSettingFromDataObjSetting(setting: any) {
    var rcSetting: RCSetting = new RCSetting();
    rcSetting.name = setting.name;
    rcSetting.value = setting.value;
    rcSetting.type = setting.Type.TypeString;
    if (rcSetting.type === "array") {
      rcSetting.index = setting.index;
    }
    if (setting.Children !== undefined && setting.Children.length > 0) {
      rcSetting.children = setting.Children.map((child: any) => {
        return RCSettingFromDataObjSetting(child);
      });
    }
    rcSetting.useValue = setting.UseValue;
    return rcSetting;
  }

  function newMessageTimeout() {
    timeOut.current = setTimeout(() => {
      timeoutNoPing();
    }, toMillisecondsFromSeconds(toSecondsFromMinutes(30)));
  }

  function toSecondsFromMinutes(minutes: number) {
    return minutes * 60;
  }

  function toMillisecondsFromSeconds(seconds: number) {
    return seconds * 1000;
  }

  useEffect(() => {
    return () => {
      if (timeOut.current) {
        clearTimeout(timeOut.current);
      }
    };
  }, []);

  function handlePlaylists(playlistsObj: any) {
    const rawPayload: IPlaylistCollectionPayload = playlistsObj;
    const collection = new PlaylistCollection(rawPayload);
    setAllPlaylists(collection);
  }

  function setGCModeById(id: string) {
    console.log(connectionStateRef.current);
    const mode = Object.values(GCState).find((mode) => mode.id === id);
    if (mode) {
      setGCMode(mode);
      checkWarningState(mode);
    } else {
      console.warn("Invalid GC mode ID:", id);
    }
  }

  function convertSecondsToTime(seconds: number) {
    var minutes = Math.floor(seconds / 60);
    var remainingSeconds = Math.floor(seconds % 60);
    minutes = Math.max(0, minutes); // Ensure minutes is not negative
    remainingSeconds = Math.max(0, remainingSeconds); // Ensure seconds is not negative
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  }

  function setWarningMessage(mode: any) {
    console.log("Setting warning message for GC mode: ", gcMode);
    if (mode === GCState.SYSTEM_PAUSED) {
      setWarning(
        new WarningMessage(
          "System Paused",
          "The headset system is paused. Please ensure the power is on and the headset is active."
        )
      );
    } else if (mode === GCState.SYSTEM_MENU_ACTIVE) {
      setWarning(
        new WarningMessage(
          "The Headset System Menu is Active",
          "On the right-hand controller, please press the Universal System Menu button to return the headset to Groove Catcher."
        )
      );
    } else if (mode === GCState.SYSTEM_OUT_OF_GUARDIAN) {
      setWarning(
        new WarningMessage(
          "Headset out of Guardian",
          "The headset is outside of the guardian area. Please return to the play area or reset the guardian."
        )
      );
    }
  }

  useEffect(() => {
    console.log("Should refresh: ", shouldRefresh);
    const socket = new WebSocket(signalingUrl);
    const peer = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    wsRef.current = socket;
    pcRef.current = peer;

    let channel = peer.createDataChannel("data");
    setUpChannel(channel);

    peer.ondatachannel = (event) => {
      console.log("Data channel created by peer:", event.channel);
      setUpChannel(event.channel);
      logItem("success", "Received data channel");
    };

    wsRef.current.onopen = () => {
      console.log("WebSocket opened");
      socket.send(
        JSON.stringify({
          type: "connect",
          connectionId: desiredConnectionId,
        })
      );
      logItem("success", "WebSocket opened, sending connect message");
    };

    // wsRef.current.onerror = (error) => {
    //   console.error("WebSocket error:", error);
    //   didFail(`${error}`);
    // }

    wsRef.current.onclose = () => {
      console.log("WebSocket closed");
      setNewConnectionState(ConnectionState.Disconnected);

      logItem("warning", "WebSocket closed");
    };

    peer.ontrack = (event) => {
      console.log("ontrack fired!");
      console.log("event.streams:", event.streams); // probably []
      console.log("event.track:", event.track);
      console.log("Track readyState:", event.track.readyState);
      console.log("Track kind:", event.track.kind);
      console.log("Track muted:", event.track.muted);
      event.track.onmute = () => {
        console.warn("The Video Track was muted, this usually means the video stream was interrupted, and groove catcher is no longer sending video");
      };

      event.track.onunmute = () => {
        console.log("Track unmuted");
        logItem("info", "Video track unmuted, ready to play");
      };
      const track = event.track;

      if (track.kind === "video") {
        track.enabled = true;
        streamRef.current.addTrack(track);

        track.onunmute = () => {
          if (videoRef.current) {
            console.log("got ref: ", track.kind);
            console.log(streamRef.current);
            videoRef.current.srcObject = streamRef.current;

            videoRef.current.onloadedmetadata = () => {
              if (videoRef.current) {
                videoRef.current
                  .play()
                  .then(() => {
                    console.log("Video playing");
                    logItem("success", "Video playing");
                    setNewConnectionState(ConnectionState.Connected);
                    sendToast("Connected to Groove Catcher", "success");
                  })
                  .catch((e) => {
                    didFail("Video play failed: " + e);
                  });
              }
            };
          }
        };
      } else if (track.kind === "audio") {
        console.log("Received audio track");

        const audioStream = new MediaStream([track]);

        if (audioRef.current) {
          audioRef.current.srcObject = audioStream;
          audioRef.current.onloadedmetadata = () => {
            if (audioRef.current) {
              audioRef.current
                .play()
                .then(() => {
                  logItem("success", "Audio playing");
                })
                .catch((e) => {
                  didFail("Audio play failed: " + e);
                });
            }
          };
        }
      }
    };

    peer.onicecandidate = (event) => {
      console.log("onice");
      logItem("info", "ICE candidate event fired");
      if (event.candidate && socket.readyState === WebSocket.OPEN) {
        const json = JSON.stringify({
          type: "candidate",
          from: desiredConnectionId,
          data: {
            connectionId: desiredConnectionId,
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex,
          },
        });
        console.log("Sending ICE candidate:", json);
        logItem("info", "Sending ICE candidate");
        socket.send(json);
      }
    };

    socket.onmessage = async (msg) => {
      const msgData = JSON.parse(msg.data);
      const { type } = msgData;
      var receivedConnectionId = "";

      console.log("Received message:", type);
      logItem("info", "Received message: " + type);

      if (!peer || peer.signalingState === "closed") {
        console.warn("RTCPeerConnection is closed. Ignoring message.");
        return;
      }

      peer.oniceconnectionstatechange = () => {
        console.log("ICE Connection State:", peer.iceConnectionState);
      };

      switch (type) {
        case "connect":
          logItem("info", "Received connection request");
          if(!validateConnectionId(msgData.connectionId)) {
            break;
          }

          receivedConnectionId = msgData.connectionId;
          logItem("info", "Connection ID: " + receivedConnectionId);
          console.log("Connected to Groove Catcher with ID:", receivedConnectionId);
          const offer = await peer.createOffer();
          await peer.setLocalDescription(offer);
          console.log("Local SDP offer:", offer.sdp);
          socket.send(
            JSON.stringify({
              type: "offer",
              data: {
                sdp: offer.sdp,
                connectionId: desiredConnectionId,
              },
              polite: false,
            })
          );
          logItem(
            "info",
            "Sending offer to Groove Catcher, data channel included"
          );
          break;
        case "offer":
          const { data, from } = msgData;
          receivedConnectionId = from;

          if (!validateConnectionId(receivedConnectionId)) {
            break;
          } else {
            //store connectionId in user preferences
            localStorage.setItem("connectionId", receivedConnectionId);
            //add connectionId to an array of connected devices
            var connectedDevices: [] = JSON.parse(
              localStorage.getItem("connectedDevices") || "[]"
            );
            if (
              !connectedDevices.some(
                (device: any) => device.connectionId === desiredConnectionId
              )
            ) {
              setNewHeadset(true);
            } else {
              setCurrentDevice(
                connectedDevices.find(
                  (device: any) => device.connectionId === desiredConnectionId
                )
              );
            }
          }

          const sdp = data.sdp;

          // if (peer.signalingState !== "stable") {
          //   console.warn("signaling state not stable, ignoring offer");
          //   return;
          // }
          await peer.setRemoteDescription(
            new RTCSessionDescription({
              type: type,
              sdp: sdp,
            })
          );

          for (const candidate of pendingIceCandidates) {
            try {
              await peer.addIceCandidate(candidate);
            } catch (error) {
              console.error("Error adding ICE candidate:", error);
              didFail(
                "Connection Lost, please retry",
                "Failed to add ICE candidate, usually due to a remote description error. This tends to happen when groove catcher returns from sleep mode and attempts auto reconnect"
              );
            }
          }

          const answer = await peer.createAnswer();
          await peer.setLocalDescription(answer);
          console.log(peer.localDescription);
          var json = JSON.stringify({
            from: receivedConnectionId,
            type: "answer",
            data: {
              sdp: answer.sdp,
              connectionId: receivedConnectionId,
            },
          });
          socket.send(json);
          console.log("Sending answer sdp:", answer.sdp);
          break;
        case "answer":
          //note that we dont check for a valid connectionId in this case.
          //This is because empirically, there is no connectionId in the answer data.
          //And we assume this is because the message is exclusive to us, and the connectionId is not necessary/is valid 
          const answersdp = msgData.data.sdp;
          try {
            await peer.setRemoteDescription(
              new RTCSessionDescription({
                type: type,
                sdp: answersdp,
              })
            );
          } catch (error) {
            console.error("Error setting remote description:", error);
            didFail(
              "Connection Failed on Remote Description, please retry",
              "Failed to set remote description, usually due to an invalid SDP. Happens on unwanted auto reconnects"
            );
          }
          break;
        case "candidate":
          if(!validateConnectionId(msgData.connectionId)) {
            break;
          }
          logItem("info", "Received ICE candidate");
          const iceData = msgData.data;
          const { candidate, sdpMid, sdpMLineIndex } = iceData;
          if (!peer.remoteDescription) {
            console.warn("No remote description set. Storing ICE candidate.");
            logItem("info", "Storing ICE candidate for later use");
            pendingIceCandidates.push(
              new RTCIceCandidate({ candidate, sdpMid, sdpMLineIndex })
            );
          } else {
            await peer.addIceCandidate(
              new RTCIceCandidate({ candidate, sdpMid, sdpMLineIndex })
            );
          }

          break;
      }

      
    };

    return () => {
      logItem("info", "Socket use finished, cleaning up...");
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current.onmessage = null; // Unsubscribe if necessary
        wsRef.current = null;
      }
      // if (pcRef.current) {
      //   pcRef.current.onicecandidate = null;
      //   pcRef.current.ontrack = null;
      //   pcRef.current.ondatachannel = null;
      //   pcRef.current.close();
      //   pcRef.current = null;
      // }

      // Optionally, clear the video element's srcObject.
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  function disconnectFromGC(aborted = false, abortedReason?: string) {
    if (timeOut.current) {
      clearTimeout(timeOut.current);
    }
    if (connectionIdMismatch) {
      /*
        If the connection ID mismatch is set, we don't want to send a disconnect
        message to Groove Catcher, as it will not recognize the connection ID.
        Instead, we just return back to the connection screen silently.
        This is to prevent Groove Catcher from trying to disconnect an invalid
        connection ID, which leads to hanging connections.
      */
      setMainAppConnection(false);
      return;
    }
    else{
      sendGCAction(ACTIONS.DISCONNECT);
      setMainAppConnection(false);
      return;
    }
    console.log("Did connection id mismatch:", connectionIdMismatch);
    if (!connectionIdMismatch) sendGCAction(ACTIONS.DISCONNECT);
    else {
      wsRef.current?.send(
        JSON.stringify({
          type: "nofail",
          connectionId: desiredConnectionId,
        })
      );
    }

    setTimeout(() => {
      if (!connectionIdMismatch) {
        wsRef.current?.send(
          JSON.stringify({
            type: "disconnect",
            connectionId: desiredConnectionId,
          })
        );
      }

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      if (dcRef.current) {
        dcRef.current.close();
        dcRef.current = null;
      }
      setNewConnectionState(ConnectionState.Disconnected);
      setShouldRefresh(true);
      if (aborted) {
        const newURL = `${window.location.origin}${
          window.location.pathname
        }?aborted=true&reason=${encodeURIComponent(
          abortedReason || "Unknown"
        )}`;
        window.location.href = newURL;
      } else {
        window.location.reload();
      }
    }, 100);
  }

  function sendGCAction(action: string, params: any[] = []) {
    // if (connectionState !== ConnectionState.Connected) {
    //   console.error("Cant send action, not connected to Groove Catcher");
    //   return;
    // }

    if (!Object.values(ACTIONS).includes(action)) {
      console.error("Invalid action:", action);
      return;
    }

    let paramsString: any[] = [];
    params.forEach((param) => {
      paramsString.push(param.toString());
    });

    sendCommandJSON("Action", action, paramsString);
  }

  function sendCommandJSON(command: string, subCommand: string, params: any[]) {
    // if (connectionState !== ConnectionState.Connected) {
    //   console.error("Cant send command, not connected to Groove Catcher");
    //   return;
    // }

    if (!dcRef.current) {
      console.error("Data channel not available");
      return;
    }

    let obj = {
      command: command,
      subcommand: subCommand,
      parameters: params,
      timestamp: new Date(),
    };
    if (dcRef.current?.readyState !== "open") {
      sendToast("Data channel closed, unable to send command", "error");
      return;
    }

    dcRef.current?.send(JSON.stringify(obj));
  }

  function getLogIcon(type: string) {
    switch (type) {
      case "error":
        return <Error color="error" />;
      case "success":
        return <CheckCircle color="success" />;
      case "warning":
        return <Warning color="warning" />;
      case "default":
        return <InfoOutlined color="primary" />;
      default:
        return <InfoOutlined />;
    }
  }

  function loadLevel(playlist: Playlist, level: Level) {
    sendGCAction(ACTIONS.LOAD_LEVEL, [playlist.UniqueID, level.UniqueID]);
    logItem("info", "Loading level: " + level.NameMenu);
    sendToast("Loading level: " + level.NameMenu, "info");
  }

  function timeoutNoPing() {
    enqueueSnackbar(
      "30 minutes since last message, please reload and retry connection",
      {
        variant: "warning",
        autoHideDuration: null,
        action: (key) => (
          <>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                console.log("refresh");
                disconnectFromGC();
              }}
            >
              Refresh
            </Button>
            <IconButton color="inherit" onClick={() => closeSnackbar(key)}>
              <Close />
            </IconButton>
          </>
        ),
      }
    );
  }

  function updateSetting(
    name: string,
    type: string,
    value: any,
    index: number
  ) {
    var params = [name, type, value];
    if (index !== -1) params.push(index);
    sendGCAction(ACTIONS.UPDATE_SETTING, params);
  }

  const [debugPanel, showDebugPanel] = useState(false);

  function actionsSection() {
    return (
      <>
        {gcMode === GCState.STOPPED ? (
          <>
            <Button
              variant="contained"
              fullWidth
              onClick={(event) => {
                setPickingLevel(true);
                setPlaylistPickerAnchorRef(event.currentTarget);
              }}
            >
              Pick Level
            </Button>
            <PlaylistWindow
              open={pickingLevel}
              allPlaylists={allPlaylists}
              setOpen={setPickingLevel}
              loadLevel={loadLevel}
              anchorEl={playlistPickerAnchorRef}
            />
          </>
        ) : null}

        <Divider variant="middle" />
        {gcMode === GCState.CATCHING_READY_TO_START ? (
          <>
            <Button
              variant="contained"
              fullWidth
              sx={{ marginTop: "0.5em" }}
              onClick={() => {
                sendGCAction(ACTIONS.START_CATCHING, ["Casual"]);
                logItem("info", "Start catching Casual");
              }}
            >
              Start Casual
            </Button>
            {/* <Button
              variant="contained"
              fullWidth
              sx={{ marginTop: "0.5em" }}
              onClick={() => {
                sendGCAction(ACTIONS.REMEASURE_PLAYER);
                logItem("info", "Remeasure Player");
              }}
            >
              Remeasure Player
            </Button> */}
            {cancelButton()}
          </>
        ) : null}
        {gcMode === GCState.CALIBRATING ? (
          <>
            {/* <Typography
              fontSize={14}
              fontWeight={"bold"}
              sx={{ marginTop: "0.5em" }}
            >
              Calibration Mode
            </Typography> */}
            {/* <Select
              defaultValue={() => {
                console.log(gcState.calibrationMode);
                //convert the string to CalibrationMode enum
                return CalibrationMode[
                  gcState.calibrationMode as keyof typeof CalibrationMode
                ];
              }}
              sx={{ width: "100%", marginRight: "0.5em" }}
            >
              {Object.values(CalibrationMode).map((mode) => (
                <MenuItem
                  key={mode}
                  value={mode}
                  onClick={() => {
                    sendGCAction(ACTIONS.SET_CALIBRATION_MODE, [mode]);
                    logItem("info", "Set calibration mode: " + mode);
                  }}
                >
                  {mode}
                </MenuItem>
              ))}
            </Select> */}
            <Button
              variant="contained"
              fullWidth
              sx={{ marginTop: "0.5em" }}
              onClick={() => {
                sendGCAction(ACTIONS.START_CALIBRATION);
                logItem("info", "Start calibration");
              }}
            >
              Start Calibration
            </Button>
            {cancelButton()}
          </>
        ) : null}
        {gcMode === GCState.CATCHING || gcMode === GCState.CATCHING_PAUSED ? (
          <div style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignContent: "center",
            alignItems: "center",
            justifyContent: "center",
            justifyItems: "center",
          }}>
              <Tooltip title="Restart Catching">
                <Button
                  variant="contained"
                  fullWidth
                  sx={{  }}
                  onClick={() => {
                    sendGCAction(ACTIONS.RESTART_CATCHING);
                    logItem("info", "Restart catching");
                  }}
                >
                  <Replay />
                </Button>
              </Tooltip>
              <Tooltip title={paused ? "Resume" : "Pause"}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ marginTop: "0.5em" }}
                  onClick={() => {
                    sendGCAction(
                      paused ? ACTIONS.RESUME_CATCHING : ACTIONS.PAUSE
                    );
                    logItem(
                      "info",
                      paused ? "Resume Catching" : "Pause catching"
                    );
                  }}
                >
                  {paused ? <PlayArrow /> : <Pause />}
                </Button>
              </Tooltip>

            <Button
              variant="contained"
              fullWidth
              sx={{ marginTop: "0.5em" }}
              color="error"
              onClick={() => {
                sendGCAction(ACTIONS.FORCE_CANCEL);
                logItem("info", "Cancel catching");
              }}
            >
              Cancel
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  function drawerContent() {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "0.4em",
            placeContent: "center",
            alignItems: "flex-start",
            flexWrap: "nowrap",
            width: "100%",
          }}
        >
          <Paper
            sx={{
              width: "100%",
              padding: 0.5,
              // marginBottom: 1,
              backgroundColor: "#A0A5A8FF",
            }}
          >
            <Typography variant="h6">
              Time: {convertSecondsToTime(gcState.currentLevelTimeSeconds)}
            </Typography>
            <Typography variant="h6">
              Total: {convertSecondsToTime(gcState.currentLevelDurationSeconds)}
            </Typography>
            <LinearProgress
              color="secondary"
              sx={{
                borderRadius: 10,
              }}
              variant="determinate"
              value={
                (gcState.currentLevelTimeSeconds /
                  gcState.currentLevelDurationSeconds) *
                100
              }
            />
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              display: "flex",
              padding: 0.5,
              width: "100%",
              marginTop: "0.5em",
              backgroundColor: "#a1a5a8",
              flexDirection: "column",
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                display: "flex",
                marginRight: "0.2em",
                paddingLeft: 0.5,
                paddingRight: 0.5,
                width: "100%",
                backgroundColor: "#909396FF",
                alignContent: "center",
                alignItems: "center",
              }}
            >
              <Typography>{currentDevice?.nickname}</Typography>
              <IconButton
                sx={{
                  width: "1.2em",
                  height: "1.2em",
                  // marginLeft: "0.2em"
                }}
                onClick={() => {
                  setNewHeadset(true);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Paper>
            <Button
              sx={{
                padding: 0.5,
                marginTop: "0.2em",
                //left align
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                width: "100%",
              }}
              onClick={() => {
                copy();
              }}
            >
              {copied ? (
                <Check fontSize="small" />
              ) : (
                <ContentCopy fontSize="small" />
              )}

              <Typography>{desiredConnectionId}</Typography>
            </Button>
          </Paper>
          {connectionState === ConnectionState.Connected ? (
            <Heading icon={<Dataset />} label="Actions" />
          ) : null}
          {actionsSection()}

          <Heading icon={<Settings />} label="Utils" />

          <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
            <Tooltip title="Version">
              <Button
                variant="contained"
                fullWidth
                sx={{ marginTop: "0.5em", marginRight: "0.5em" }}
                onClick={() => {
                  sendToast("Version 0.1_2023-02-06-20-20");
                  // openVersion(true);
                }}
              >
                <History />
              </Button>
            </Tooltip>
            <Tooltip title="View Log">
              <Button
                variant="contained"
                fullWidth
                sx={{ marginTop: "0.5em" }}
                onClick={() => {
                  setLogOpen(true);
                }}
              >
                <ReceiptLong />
              </Button>
            </Tooltip>
          </div>
          <Button
            variant="contained"
            fullWidth
            sx={{ marginTop: "0.5em" }}
            onClick={(event) => {
              setShowSettings(true);
              setSettingsAnchorRef(event.currentTarget);
            }}
          >
            <Settings />
            Settings
          </Button>
          <SettingsWindow
            open={showSettings}
            setOpen={setShowSettings}
            settings={settings}
            anchorEl={settingsAnchorRef}
            updateSetting={updateSetting}
          />
          {IS_DEV ? (
            <>
              <Heading icon={<Build />} label="Dev"></Heading>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  showDebugPanel(true);
                }}
              >
                Debug Panel
              </Button>
              <Tooltip title="View Log">
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ marginTop: "0.5em" }}
                  onClick={() => {
                    setDeviceLogOpen(true);
                  }}
                >
                  Device Log
                </Button>
              </Tooltip>
              <Modal open={debugPanel} onClose={() => showDebugPanel(false)}>
                <Fade in={debugPanel}>
                  <Paper
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      padding: 2,
                      width: "70%",
                      height: "80%",
                      display: "flex",
                      flexDirection: "column",
                      overflowY: "auto",
                      backgroundColor: glassColor(),
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ marginBottom: 2 }}
                      color="secondary"
                      fontWeight="bold"
                    >
                      Debug Panel
                    </Typography>
                    <Typography color="secondary">
                      Connection State: {connectionState}
                    </Typography>
                    {connectionState === ConnectionState.Error ? (
                      <Typography color="error">
                        Error Reason: {failReason}
                      </Typography>
                    ) : null}
                    <Divider sx={{ marginY: 1 }} />

                    <Typography color="secondary">
                      Current Device: {currentDevice?.nickname || "None"}
                    </Typography>
                    <Typography color="secondary">
                      Local Connection ID: {desiredConnectionId}
                    </Typography>
                    <Typography color="secondary">
                      Local Volume:{" "}
                      {localStorage.getItem("local-volume") || "Not set"}%
                    </Typography>
                    <Divider sx={{ marginY: 1 }} />

                    <Typography color="secondary">
                      Current GC Mode: {gcMode.displayName} ({gcMode.id})
                    </Typography>
                    <Typography color="secondary">
                      System Menu Active:{" "}
                      {gcMode === GCState.SYSTEM_MENU_ACTIVE ? "Yes" : "No"}
                    </Typography>
                    <Typography color="secondary">
                      System Paused:{" "}
                      {gcMode === GCState.SYSTEM_PAUSED ? "Yes" : "No"}
                    </Typography>
                    <Typography color="secondary">
                      System Out of Guardian:{" "}
                      {gcMode === GCState.SYSTEM_OUT_OF_GUARDIAN ? "Yes" : "No"}
                    </Typography>
                    <Accordion
                      sx={{
                        backgroundColor: whiteGlassColor(true, 0.3),
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography fontWeight="bold">Full State</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {Object.entries(gcState).map(([key, value]) => (
                          <div>
                            <strong>{key}:</strong> {JSON.stringify(value)}
                          </div>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                    <Divider sx={{ marginY: 1 }} />

                    <Typography color="secondary" fontWeight={"bold"}>
                      Network
                    </Typography>
                    <Typography color="secondary">
                      WebSocket URL: {wsRef.current?.url}
                    </Typography>
                    <Typography color="secondary">
                      Pending ICE Candidates: {pendingIceCandidates.length}
                    </Typography>
                    <Typography color="secondary">
                      WebSocket Ready State: {wsRef.current?.readyState}
                    </Typography>
                    <Typography color="secondary">
                      Data Channel Ready State: {dcRef.current?.readyState}
                    </Typography>
                    <Typography color="secondary">
                      Data Channel Label: {dcRef.current?.label}
                    </Typography>
                    <Typography color="secondary">
                      Peer Connection ICE Connection State:{" "}
                      {pcRef.current?.iceConnectionState}
                    </Typography>
                    <Typography color="secondary">
                      Peer Connection Signaling State:{" "}
                      {pcRef.current?.signalingState}
                    </Typography>
                    <Divider sx={{ marginY: 1 }} />

                    <Typography color="secondary" fontWeight={"bold"}>
                      Log Total: {log.length}
                    </Typography>
                    <Typography color="secondary">
                      Info: {log.filter((l) => l.type === "info").length}
                    </Typography>
                    <Typography color="secondary">
                      Success: {log.filter((l) => l.type === "success").length}
                    </Typography>
                    <Typography color="secondary">
                      Warning: {log.filter((l) => l.type === "warning").length}
                    </Typography>
                    <Typography color="secondary">
                      Error: {log.filter((l) => l.type === "error").length}
                    </Typography>
                    <Divider sx={{ marginY: 1 }} />
                  </Paper>
                </Fade>
              </Modal>
            </>
          ) : null}
          <Modal
            open={deviceLogOpen}
            onClose={() => {
              setDeviceLogOpen(false);
            }}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Fade in={deviceLogOpen}>
              <Paper
                variant="elevation"
                elevation={3}
                sx={{
                  display: "flex",
                  padding: 0.5,
                  width: "80%",
                  marginTop: "0.5em",
                  height: "80%",
                  overflowY: "scroll",
                  flexDirection: "column",
                  backgroundColor: whiteGlassColor(),
                  backdropFilter: "blur(10px)",
                }}
              >
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <Typography variant="h5" sx={{ padding: 1 }}>
                    Device Log
                  </Typography>
                </div>

                <Divider variant="fullWidth" />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {deviceLog.map((line, index) => {
                    return (
                      <Container
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          paddingLeft: 0,
                          alignItems: "center",
                          alignContent: "center",
                        }}
                        key={index}
                      >
                        <Typography key={index} variant="body2">
                          {line}
                        </Typography>
                      </Container>
                    );
                  })}
                </Box>
              </Paper>
            </Fade>
          </Modal>
          <Modal
            open={logOpen}
            onClose={() => {
              setLogOpen(false);
            }}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Fade in={logOpen}>
              <Paper
                variant="elevation"
                elevation={3}
                sx={{
                  display: "flex",
                  padding: 0.5,
                  width: "80%",
                  marginTop: "0.5em",
                  height: "80%",
                  overflowY: "scroll",
                  flexDirection: "column",
                  backgroundColor: whiteGlassColor(),
                  backdropFilter: "blur(10px)",
                }}
              >
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <Typography variant="h5" sx={{ padding: 1 }}>
                    Information and Error Log
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ padding: 1, marginBottom: 0.5 }}
                    onClick={() => {
                      exportLog();
                    }}
                  >
                    <Upload />
                    Export Log
                  </Button>
                </div>

                <Divider variant="fullWidth" />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {log.map((line, index) => {
                    return (
                      <Container
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          paddingLeft: 0,
                          alignItems: "center",
                          alignContent: "center",
                        }}
                        key={index}
                      >
                        {getLogIcon(line.type)}
                        <Typography key={index} variant="body2">
                          {line.message}
                        </Typography>
                      </Container>
                    );
                  })}
                </Box>
              </Paper>
            </Fade>
          </Modal>
          <Snackbar
            open={versionOpen}
            message="Version 0.1_2023-02-06-20-20"
            autoHideDuration={3000}
            onClose={versionClose}
            action={
              <Button color="secondary" onClick={versionClose}>
                OK
              </Button>
            }
          />
        </Box>
      </>
    );
  }

  function toggleFullscreen() {
    setIsFullscreen(!isFullscreen);
    if(isFullscreen){
      document.exitFullscreen();
    }
    topLevelElement.current?.requestFullscreen().catch((err) => {
      console.error("Error attempting to enable full-screen mode:", err);
      sendToast("Error attempting to enable full-screen mode", "error");
    });
  }

  return (
    <div style={{
      display: "flex",
      width: "100%",
      height: "100%",
    }} ref={topLevelElement}>
      <Button
        variant="contained"
        sx={{
          position: "absolute",
          bottom: "1em",
          right: "1em",
          zIndex: 1000,
        }}
        onClick={toggleFullscreen}
      >
        {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
      </Button>
      <Box sx={{ flexGrow: 1, width: "100%" }}>
        <HeadsetModal
          open={newHeadset}
          setOpen={setNewHeadset}
          onSubmit={(nickname: string) => {
            var name = nickname === "noname" ? desiredConnectionId : nickname;
            var connectedDevices = JSON.parse(
              localStorage.getItem("connectedDevices") || "[]"
            );
            if (
              !connectedDevices.some(
                (device: any) => device.connectionId === desiredConnectionId
              )
            ) {
              connectedDevices.push({
                connectionId: desiredConnectionId,
                nickname: name,
              });
            } else {
              var device = connectedDevices.find(
                (device: any) => device.connectionId === desiredConnectionId
              );
              if (device && name !== desiredConnectionId) {
                device.nickname = name;
              }
            }

            localStorage.setItem(
              "connectedDevices",
              JSON.stringify(connectedDevices)
            );
            if (desiredConnectionId && name) {
              setCurrentDevice(new Device(desiredConnectionId, name));
            } else {
              console.error("localConnectionId is undefined");
            }
          }}
        />
        {connectionState == ConnectionState.Warning ? (
          <InfoModal message={warning} />
        ) : null}

        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Slide direction="down" in={showToolbarMobile}>
            <AppBar sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
              <Toolbar
                sx={{
                  display: "flex",
                  minHeight: "150px",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  <Typography
                    variant={isMobile ? "body2" : "body1"}
                    marginRight={"1em"}
                  >
                    Level: {gcState.currentLevelNameMenu}
                  </Typography>
                  <BrowserView>
                    {" "}
                    <Typography marginRight={"1em"}>|</Typography>
                  </BrowserView>
                  <Typography
                    variant={isMobile ? "body2" : "body1"}
                    marginRight={"1em"}
                  >
                    Song: {gcState.currentSongDisplayName}
                  </Typography>
                  {/* <Typography marginRight={"1em"}>|</Typography> */}
                  {/* <Typography marginRight={"1em"}>
                Debug GCMODE: {gcMode.displayName} ({gcMode.id})
              </Typography> */}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {/* <VolumeSlider
                value={gcState.systemVolume}
                onChange={(value) => {
                  sendGCAction(ACTIONS.SET_SYSTEM_VOLUME, [value]);
                  logItem("info", "System volume set to: " + value);
                }}
              /> */}
                  <LocalSettings />
                  <VolumeSlider />
                  <FormGroup row>
                    {/* <FormControlLabel
                  control={<Switch color="secondary" defaultChecked />}
                  label="Video"
                  style={{ marginLeft: "1em" }}
                  onChange={(e, checked) => {
                    sendGCAction(ACTIONS., [checked]);
                    logItem("info", "Headset UI set to: " + checked);
                  }}
                /> */}
                    <FormControlLabel
                      control={<Switch color="secondary" defaultChecked />}
                      label= {`Show Score: ${gcState.currentScore}`}
                      style={{ marginLeft: "1em" }}
                      onChange={(e, checked) => {
                        sendGCAction(ACTIONS.SET_SHOWSCORE, [checked]);
                        logItem("info", "Show score set to: " + checked);
                      }}
                    />
                    <Typography>
                    
                  </Typography>
                    <FormControlLabel
                      control={<Switch color="secondary" defaultChecked />}
                      label="Headset UI"
                      style={{ marginLeft: "1em" }}
                      onChange={(e, checked) => {
                        sendGCAction(ACTIONS.SET_SHOW_UI, [checked]);
                        logItem("info", "Headset UI set to: " + checked);
                      }}
                    />
                  </FormGroup>
                  
                  <Button
                    color="error"
                    onClick={() => {
                      disconnectFromGC();
                    }}
                  >
                    Disconnect
                  </Button>
                </Box>
              </Toolbar>
            </AppBar>
          </Slide>
          <MobileView
            style={{
              position: "absolute",
              top: showToolbarMobile ? 70 : 5,
              right: 5,
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setShowToolbarMobile(!showToolbarMobile);
              }}
              sx={{
                zIndex: 1000,
              }}
            >
              {showToolbarMobile ? <ArrowUpward /> : <ArrowDownward />}
            </Button>
          </MobileView>
          <BrowserView>
            <Drawer
              variant="permanent"
              sx={{
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                  width: `${drawerWidth}`,
                  boxSizing: "border-box",
                  flexWrap: "nowrap",
                  bgcolor: "#6e7376",
                },
              }}
            >
              <Toolbar />
              {drawerContent()}
            </Drawer>
          </BrowserView>
        </Box>
        <Box
          component="main"
          sx={{
            display: "flex",
            width: isMobile ? "100%" : "90%",
            height: "100%",
            flexGrow: 1,
            bgcolor: "black",
            marginLeft: isMobile ? 0 : `${drawerWidth}`,
          }}
        >
          <>
            <div
              style={{
                width:
                  connectionState === ConnectionState.Connected ||
                  connectionState === ConnectionState.Warning
                    ? "100%"
                    : "0%",
                height: "100%",
              }}
            >
              <MobileView style={{}}>
                <Paper
                  sx={{
                    position: "absolute",
                    top: !showToolbarMobile ? 50 : 115,
                    right: 5,
                    padding: 2,
                    backgroundColor: glassColor(),
                    backdropFilter: "blur(10px)",
                    width: "200px",
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  <Accordion
                    sx={{
                      backgroundColor: glassColor(true, 0.3),
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography color="secondary" fontWeight={"bold"}>
                        Actions
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>{actionsSection()}</AccordionDetails>
                  </Accordion>

                  <Button
                    variant="contained"
                    onClick={(event) => {
                      setMoreAnchorEl(event.currentTarget);
                    
                      setShowDrawerContentMobile(!showDrawerContentMobile)
                    }}
                    sx={{
                      marginTop: "0.5em",
                    }}
                    
                  >
                    More
                  </Button>
                </Paper>
              </MobileView>
              <Popover
                anchorEl={moreAncholEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                open={showDrawerContentMobile}
                onClose={() => setShowDrawerContentMobile(false)}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Paper
                  sx={{
                    backgroundColor: glassColor(),
                    backdropFilter: "blur(10px)",
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <IconButton color="secondary">
                    <Close onClick={() => setShowDrawerContentMobile(false)} />
                  </IconButton>
                  {drawerContent()}
                </Paper>
              </Popover>
              <Alert
                severity="info"
                variant="standard"
                elevation={3}
                onClick={() => {
                  setShowStateInfo(!showStateInfo);
                }}
                sx={{
                  width: showStateInfo ? 300 : 38,
                  padding: 0,
                  paddingLeft: 1,
                  position: "absolute",
                  top: showToolbarMobile ? `${69}px` : "5px", // Adjust based on AppBar height and Toolbar height
                  left: !isMobile ? `${10.5}%` : 5, // Adjust based on Drawer width
                  zIndex: 1000,
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "width 0.3s ease-in-out",
                }}
              >
                {showStateInfo ? (
                  <>
                    {showText ? (
                      <Fade in={showText}>
                        <Typography>{gcMode.displayName}</Typography>
                      </Fade>
                    ) : null}

                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowStateInfo(false);
                      }}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        right: "0px",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <ChevronLeft />
                    </IconButton>
                  </>
                ) : null}
              </Alert>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", height: "auto" }}
              />
              <audio ref={audioRef} autoPlay playsInline />
            </div>
          </>
          {connectionState === ConnectionState.Disconnected ? (
            <CircularProgress
              color="secondary"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
              }}
            />
          ) : null}

          {connectionState === ConnectionState.Error ? (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                gap: "1em",
              }}
            >
              <Typography variant="h4" color="secondary" fontWeight={"bold"}>
                Failed to connect to Groove Catcher
              </Typography>
              <Typography variant="h5" color="secondary">
                {failReason}
              </Typography>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  alignContent: "center",
                  justifyItems: "center",
                  flexDirection: "column",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    disconnectFromGC();
                  }}
                >
                  Try Again
                </Button>
                <Button variant="contained" color="primary" sx={{marginTop: "1em"}} onClick={() => {setShowFix(true)}}>
                  How to fix this?
                </Button>
                <Modal open={showFix} onClose={() => setShowFix(false)}>
                  <Paper
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      padding: 2,
                      width: "70%",
                      height: "80%",
                      display: "flex",
                      flexDirection: "column",
                      overflowY: "auto",
                      backgroundColor: glassColor(),
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Typography color="secondary" sx={{
                      fontWeight: "bold",
                      fontSize: "1.5em"
                    }}>How to fix this?</Typography>
                    <Typography color="secondary" sx={{
                      marginTop: "1em"
                    }}>
                      1. Make sure Groove Catcher is connected to the same network as this app. Both apps must be using the same network to acheive a proper connection.
                    </Typography>
                    <Typography color="secondary" sx={{
                      marginTop: "1em"
                    }}>
                      2. Make sure Groove Catcher is running and that the headset has not gone to sleep.
                    </Typography>
                    <Typography color="secondary" sx={{
                      marginTop: "1em"
                    }}>
                      3. Look to the right and Press the "Reset" button in Groove Catcher under the remote control section to reset the connection.
                    </Typography>
                    <Typography color="secondary" sx={{
                      marginTop: "1em"
                    }}>
                      4. If the problem persists, try restarting Groove Catcher and reload this browser window.
                    </Typography>
                  </Paper>
                </Modal>
              </div>
            </div>
          ) : null}
        </Box>
      </Box>
    </div>
  );
}
